import { useMemo } from 'react';
import { useRouter } from 'next/router';
import { useEchainProductStore } from './states/useEchainProductStore';
import Locale from '@formatjs/intl-locale';
import type { EicShopSubmitPayloadV1 } from '@igus/echain-shared-exchange';
import getConfig from 'next/config';
import type { NextRuntimeConfiguration } from '@libs/config';

const { publicRuntimeConfig } = getConfig() as NextRuntimeConfiguration;

export const useIAConfigurator = (chainId: string, chainLength: number) => {
  if (!publicRuntimeConfig.interiorSeparationConfiguratorUrl) {
    throw new Error('INTERIOR_SEPARATION_CONFIGURATOR_URL is missing');
  }

  const iaConfig = useEchainProductStore(store => store.eChainPartsList?.iaConfig);

  const getIaConfiguratorWindow = useEchainProductStore(store => store.getIaConfiguratorWindow);
  const setIaConfiguratorWindow = useEchainProductStore(store => store.setIaConfiguratorWindow);

  const { locale } = useRouter();

  const languageCultureQuery = useMemo(() => {
    if (!locale) {
      return '';
    }
    const localeObj = new Locale(locale);
    if (!localeObj.language && !localeObj.region) {
      return '';
    }
    if (!localeObj.region) {
      // language is available but region isn't
      return `&l=${localeObj.language}`;
    }
    // query with only region has no effect, so it is unnecessary
    return `&l=${localeObj.language}&c=${localeObj.region}`;
  }, [locale]);

  // TODO: Endpoint will get a version flag in the future. Discuss with echain team when this is implemented and add it to the ia configurator call url
  // configId must be base64 encoded. in the future this will be changed for chain and chainlength as well, but currently it works only with plain strings

  const iaConfiguratorUrl = publicRuntimeConfig.interiorSeparationConfiguratorUrl;

  const url = `${iaConfiguratorUrl}?readonly=true&chain=${chainId}&chainlength=${chainLength}${languageCultureQuery}&caller=shop`;

  let igusGUId = '';

  if (iaConfig?.configJson) {
    const iaConfigPayload = JSON.parse(iaConfig?.configJson) as EicShopSubmitPayloadV1;
    igusGUId = iaConfigPayload?.exchangeConfiguration?.IgusGUId;
    if (!igusGUId) {
      throw new Error('No valid IgusGUId found in exchangeConfiguration');
    }
  }

  const editConfigId = igusGUId ? Buffer.from(igusGUId).toString('base64') : '';
  const editUrl = `${iaConfiguratorUrl}?readonly=true${languageCultureQuery}&caller=shop&configid=${editConfigId}`;

  const closeIaConfiguratorWindow = (windowRef: Window | null = null): Window | null => {
    let windowRefToClose: Window | null = windowRef || getIaConfiguratorWindow().windowRef;

    if (!windowRefToClose) {
      return null;
    }

    windowRefToClose.close();
    windowRefToClose = null;
    setIaConfiguratorWindow(windowRefToClose);

    return windowRefToClose;
  };

  const openIaConfiguratorWindow = (edit: boolean) => {
    const configuratorWindow = getIaConfiguratorWindow();

    if (configuratorWindow.windowRef) {
      // due to cross-origin restrictions we can't add an eventlistener to the configuratorWindow.
      // Therefore, we have to check here if the tab with the configurator is still open
      if (!configuratorWindow.windowRef.closed) {
        // just jump back to the already open window
        configuratorWindow.windowRef.focus();
        return;
      }
      configuratorWindow.windowRef = closeIaConfiguratorWindow(configuratorWindow.windowRef);
    }

    // open a new window
    setIaConfiguratorWindow(window.open(edit ? editUrl : url, '_blank'));
  };

  return {
    isIaConfiguratorWindowOpen: Boolean(getIaConfiguratorWindow().windowRef),
    closeIaConfiguratorWindow,
    openIaConfiguratorWindow,
  };
};
